import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, Sparkles, CreditCard, Download, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * « Je crée ma caricature » — espace client. GAMME 4 OFFRES (Carlos 2026-09-16). Le style, le prix, la qualité, le
 * nombre d'images et la personnalisation sont fixés CÔTÉ SERVEUR ; le frontend n'envoie que le code d'offre et,
 * uniquement pour PREMIUM_HD_TRIO_CUSTOM, des instructions facultatives. Photo → offre → (perso) → paiement SumUp
 * (page hébergée) → retour ?session_id → vérification → génération image→image (1 ou 3 variantes). Aucune clé côté
 * navigateur ; tout passe par /api/client/caricature/*.
 */
const OFFER_FALLBACK = [
  { id: 'STANDARD_SOLO', price_eur: 1.01, quality: 'medium', image_count: 1, custom: false },
  { id: 'PREMIUM_HD_SOLO', price_eur: 1.21, quality: 'high', image_count: 1, custom: false },
  { id: 'STANDARD_PACK', price_eur: 1.50, quality: 'medium', image_count: 5, custom: false },
  { id: 'PREMIUM_HD_TRIO_CUSTOM', price_eur: 2.40, quality: 'high', image_count: 3, custom: true },
];
const BADGE = { STANDARD_PACK: 'caric.badge.bestPrice', PREMIUM_HD_TRIO_CUSTOM: 'caric.badge.custom' };

const ClientCaricaturePage = () => {
  const { api } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fileRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [offer, setOffer] = useState('STANDARD_SOLO');
  const [customText, setCustomText] = useState('');
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | paying | verifying | generating | done | error
  const [results, setResults] = useState([]); // [{ slot, src }]
  const [resultSid, setResultSid] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [consent, setConsent] = useState(false);
  const [rights, setRights] = useState(false);
  const [compareOk, setCompareOk] = useState(true);

  const offers = (config?.offers?.length ? config.offers : OFFER_FALLBACK);
  const sel = offers.find((o) => o.id === offer) || OFFER_FALLBACK[0];
  const isCustom = !!sel.custom;
  const customMax = config?.custom_max || 300;

  const loadHistory = useCallback(async () => {
    try { const r = await api.get('/client/caricature/history'); setHistory(r.data || []); } catch (_) {}
  }, [api]);

  useEffect(() => {
    api.get('/client/caricature/config').then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false, offers: OFFER_FALLBACK }));
    loadHistory();
  }, [api, loadHistory]);

  // Récupère toutes les images (emplacements) d'une session via jetons signés temporaires.
  const fetchImages = useCallback(async (sid, token, images) => {
    const out = [];
    for (const im of (images || [])) {
      try {
        const r = await api.get(`/client/caricature/${sid}/image`, { params: { token, slot: im.slot } });
        out.push({ slot: im.slot, src: `data:image/png;base64,${r.data.image_base64}` });
      } catch (_) {}
    }
    return out;
  }, [api]);

  // Retour de SumUp : ?session_id=… → vérifier → générer (idempotent, emplacements) → afficher les images.
  useEffect(() => {
    const sid = params.get('session_id');
    if (!sid) return;
    if (config && !config.enabled) return;
    let cancelled = false;
    (async () => {
      setPhase('verifying'); setError(''); setBusy(true);
      try {
        const v = await api.get(`/client/caricature/verify/${sid}`);
        if (cancelled) return;
        if (!v.data.is_paid) { setPhase('error'); setError(t('caric.notPaid')); return; }
        setPhase('generating');
        const g = await api.post(`/client/caricature/generate/${sid}`, {}, { timeout: 360000 });
        if (cancelled) return;
        const imgs = await fetchImages(sid, g.data.image_token, g.data.images);
        setResults(imgs); setResultSid(sid); setPhase('done'); loadHistory();
      } catch (e) {
        if (!cancelled) { setPhase('error'); setError(e.response?.data?.detail || t('caric.genError')); }
      } finally {
        if (!cancelled) setBusy(false);
        window.history.replaceState({}, document.title, '/espace-client/caricature');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = (file) => {
    if (!file || !file.type.startsWith('image/')) { setError(t('caric.invalidFile')); return; }
    if (file.size > 12 * 1024 * 1024) { setError(t('caric.tooBig')); return; }
    const reader = new FileReader();
    reader.onload = (e) => { setPreview(e.target.result); setB64(String(e.target.result).split(',')[1]); setResults([]); setError(''); };
    reader.readAsDataURL(file);
  };

  const pay = async () => {
    if (!b64) { setError(t('caric.needPhoto')); return; }
    if (!consent) { setError(t('caric.consentRequired')); return; }
    if (!rights) { setError(t('caric.rightsRequired')); return; }
    setBusy(true); setPhase('paying'); setError('');
    try {
      const uiLang = (() => { try { return localStorage.getItem('app_lang') || 'fr'; } catch (_) { return 'fr'; } })();
      const r = await api.post('/client/caricature/checkout', {
        offer, image_base64: b64, consent: true, rights_confirm: true,
        custom_instructions: isCustom ? (customText || '').slice(0, customMax) : '', lang: uiLang,
      });
      window.location.href = r.data.checkout_url; // page de paiement SumUp hébergée
    } catch (e) {
      setPhase('idle'); setBusy(false); setError(e.response?.data?.detail || t('caric.payError'));
    }
  };

  const downloadOne = (src, i) => {
    const a = document.createElement('a'); a.href = src; a.download = `caricature-${i + 1}-${Date.now()}.png`; a.click();
  };
  const openHistory = async (item) => {
    const imgs = await fetchImages(item.session_id, item.image_token, item.images);
    setResults(imgs); setResultSid(item.session_id); setPhase('done'); window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const removeCaricature = async (sid) => {
    if (!window.confirm(t('caric.deleteConfirm'))) return;
    try { await api.delete(`/client/caricature/${sid}`); if (resultSid === sid) { setResults([]); setPhase('idle'); } loadHistory(); } catch (_) {}
  };

  const offerName = (id) => t(`caric.o.${id}.name`);
  const offerDesc = (id) => t(`caric.o.${id}.desc`);
  const qualityLabel = (q) => (q === 'high' ? t('caric.qualityHigh') : t('caric.qualityMedium'));
  const imagesLabel = (n) => (n > 1 ? t('caric.imagesMany', { n }) : t('caric.imageOne'));
  // Nom d'offre = « N caricature(s) Type [personnalisées] » (déjà la quantité + le type) ; le prix des packs
  // s'affiche « pour {total} » -> jamais « prix × N », jamais de prix unitaire ambigu.
  const priceLine = (o) => (o.image_count > 1
    ? t('caric.forTotal', { price: formatCurrency(o.price_eur) })
    : formatCurrency(o.price_eur));

  return (
    <div className="min-h-screen px-4 py-6" data-testid="client-caricature-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <button type="button" onClick={() => navigate('/espace-client')} className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white" data-testid="caric-back">
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" aria-hidden="true" /> {t('caric.back')}
        </button>
        <header>
          <h1 className="text-2xl sm:text-3xl font-black text-white flex items-center gap-2"><Sparkles className="w-6 h-6 text-pink-400" aria-hidden="true" /> {t('caric.title')}</h1>
          <p className="text-sm text-gray-400 mt-1">{t('caric.subtitle', { price: formatCurrency(1.01) })}</p>
        </header>

        {config && !config.enabled && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-amber-200 text-sm" data-testid="caric-disabled">{t('caric.disabled')}</div>
        )}

        {(phase === 'done' && results.length > 0) && (
          <section className="rounded-2xl border border-pink-500/30 bg-black/40 p-4 space-y-3" data-testid="caric-result">
            <h2 className="text-white font-bold">{t('caric.yourCaricature')} · {results.length > 1 ? imagesLabel(results.length) : ''}</h2>
            <div className={`grid ${results.length > 1 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1'} gap-3`}>
              {results.map((im, i) => (
                <figure key={im.slot} className="space-y-2">
                  <img src={im.src} alt={`${t('caric.yourCaricature')} ${i + 1}`} className="w-full rounded-xl border border-white/10" />
                  <button type="button" onClick={() => downloadOne(im.src, i)} className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg bg-pink-600 text-white text-xs font-semibold hover:bg-pink-500" data-testid={`caric-download-${im.slot}`}><Download className="w-3.5 h-3.5" /> {t('caric.download')}</button>
                </figure>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 justify-center pt-1">
              <button type="button" onClick={() => { setResults([]); setPhase('idle'); }} className="px-4 py-2 rounded-lg bg-white/10 text-gray-200 text-sm hover:bg-white/20">{t('caric.another')}</button>
              {resultSid && <button type="button" onClick={() => removeCaricature(resultSid)} className="px-4 py-2 rounded-lg bg-white/5 text-gray-400 text-sm hover:text-red-300" data-testid="caric-result-del">{t('caric.delete')}</button>}
            </div>
          </section>
        )}

        {(phase === 'verifying' || phase === 'generating') && (
          <section className="rounded-2xl border border-white/10 bg-black/40 p-6 text-center space-y-2" data-testid="caric-progress">
            <ImageIcon className="w-10 h-10 mx-auto text-pink-400 animate-pulse" aria-hidden="true" />
            <p className="text-white font-semibold">{phase === 'verifying' ? t('caric.verifying') : t('caric.generating')}</p>
            <p className="text-xs text-gray-500">{sel.image_count > 1 ? t('caric.generatingHintPack') : t('caric.generatingHint')}</p>
          </section>
        )}

        {error && <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-red-200 text-sm" data-testid="caric-error">{error}</div>}

        {phase !== 'verifying' && phase !== 'generating' && !(config && !config.enabled) && (
          <>
            <section
              className={`rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${preview ? 'border-pink-500/50' : 'border-white/15 hover:border-pink-500/40'}`}
              onClick={() => fileRef.current?.click()}
              onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
              onDragOver={(e) => e.preventDefault()}
              role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
              aria-label={t('caric.upload')} data-testid="caric-upload">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid="caric-file" />
              {preview ? (
                <img src={preview} alt="" className="max-h-72 mx-auto rounded-xl" />
              ) : (
                <div className="space-y-2">
                  <Upload className="w-10 h-10 mx-auto text-gray-400" aria-hidden="true" />
                  <p className="text-white font-semibold">{t('caric.upload')}</p>
                  <p className="text-xs text-gray-500">{t('caric.uploadHint')}</p>
                </div>
              )}
            </section>

            <section className="space-y-3" data-testid="caric-offers">
              <h2 className="text-white font-bold">{t('caric.chooseOffer')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {offers.map((o) => {
                  const selected = offer === o.id;
                  return (
                    <button key={o.id} type="button" onClick={() => setOffer(o.id)} aria-pressed={selected}
                      className={`relative text-start rounded-2xl border p-4 ${selected ? 'border-pink-500 ring-2 ring-pink-500/40 bg-pink-500/[.06]' : 'border-white/10 hover:border-white/30 bg-black/30'}`}
                      data-testid={`caric-offer-${o.id}`}>
                      {BADGE[o.id] && (
                        <span className="absolute -top-2 end-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400 text-black" data-testid={`caric-badge-${o.id}`}>{t(BADGE[o.id])}</span>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-white font-bold">{offerName(o.id)}</span>
                        <span className="text-pink-300 font-bold whitespace-nowrap">{priceLine(o)}</span>
                      </div>
                      <p className="text-xs text-gray-300 mt-1">{offerDesc(o.id)}</p>
                      <p className="text-[11px] text-gray-400 mt-1">{imagesLabel(o.image_count)} · {qualityLabel(o.quality)}{o.custom ? ` · ${t('caric.customizable')}` : ''}</p>
                    </button>
                  );
                })}
              </div>

              {isCustom && (
                <div className="rounded-2xl border border-fuchsia-500/30 bg-fuchsia-500/[.06] p-4 space-y-2" data-testid="caric-custom">
                  <label htmlFor="caric-custom-input" className="text-white font-semibold text-sm block">{t('caric.custom.label')}</label>
                  <p className="text-xs text-gray-300">{t('caric.custom.help')}</p>
                  <textarea id="caric-custom-input" value={customText} onChange={(e) => setCustomText(e.target.value.slice(0, customMax))} maxLength={customMax}
                    placeholder={t('caric.custom.placeholder')} rows={3}
                    className="w-full rounded-lg bg-black/40 border border-white/15 p-2 text-sm text-gray-100 placeholder-gray-500 focus:border-fuchsia-400 outline-none" data-testid="caric-custom-input" />
                  <div className="flex items-center justify-between text-[11px] text-gray-500">
                    <span>{t('caric.custom.examples')}</span><span>{(customText || '').length}/{customMax}</span>
                  </div>
                </div>
              )}

              {compareOk && (
                <div className="rounded-2xl border border-white/10 bg-black/30 p-3" data-testid="caric-compare">
                  <p className="text-xs text-gray-400 mb-2">{t('caric.compareTitle')}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <figure className="space-y-1">
                      <img src="/aide-media/caricature-medium.png" alt={t('caric.compareAltStandard')} className="w-full rounded-lg border border-white/10" loading="lazy" onError={() => setCompareOk(false)} />
                      <figcaption className="text-[11px] text-gray-400 text-center">{t('caric.qualityMedium')}</figcaption>
                    </figure>
                    <figure className="space-y-1">
                      <img src="/aide-media/caricature-high.png" alt={t('caric.compareAltPremium')} className="w-full rounded-lg border border-white/10" loading="lazy" onError={() => setCompareOk(false)} />
                      <figcaption className="text-[11px] text-gray-400 text-center">{t('caric.qualityHigh')}</figcaption>
                    </figure>
                  </div>
                  <p className="text-[11px] text-gray-500 text-center mt-2">{t('caric.compareNote')}</p>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-amber-500/30 bg-amber-500/[.06] p-4 text-sm space-y-2" data-testid="caric-consent">
              <h2 className="text-white font-bold">{t('caric.consentTitle')}</h2>
              <ul className="list-disc pl-5 text-gray-300 text-xs space-y-0.5">
                <li>{t('caric.consentAi')}</li>
                <li>{t('caric.consentPurpose')}</li>
                <li>{t('caric.consentRetention')}</li>
                <li>{t('caric.consentDelete')}</li>
                <li>{t('caric.consentRights')}</li>
                <li>{t('caric.consentMinors')}</li>
              </ul>
              <label className="flex items-start gap-2 text-gray-200 cursor-pointer pt-1">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="caric-consent-check" />
                <span>{t('caric.consentAccept')}</span>
              </label>
              <label className="flex items-start gap-2 text-gray-200 cursor-pointer">
                <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} className="mt-0.5" data-testid="caric-rights-check" />
                <span>{t('caric.rightsConfirm')}</span>
              </label>
            </section>

            {/* Récapitulatif avant paiement */}
            <section className="rounded-2xl border border-white/10 bg-black/40 p-5 space-y-3" data-testid="caric-recap">
              <h2 className="text-white font-bold text-sm">{t('caric.recap.title')}</h2>
              <dl className="text-sm text-gray-300 space-y-1">
                <div className="flex justify-between"><dt>{t('caric.recap.offer')}</dt><dd className="text-white font-semibold" data-testid="caric-recap-offer">{offerName(sel.id)}</dd></div>
                <div className="flex justify-between"><dt>{t('caric.recap.images')}</dt><dd className="text-white">{imagesLabel(sel.image_count)}</dd></div>
                <div className="flex justify-between"><dt>{t('caric.recap.quality')}</dt><dd className="text-white">{qualityLabel(sel.quality)}</dd></div>
                <div className="flex justify-between"><dt>{t('caric.recap.custom')}</dt><dd className="text-white">{sel.custom ? t('caric.recap.customYes') : t('caric.recap.customNo')}</dd></div>
                <div className="flex justify-between border-t border-white/10 pt-1 mt-1"><dt className="font-semibold">{t('caric.recap.total')}</dt><dd className="text-pink-300 font-bold text-base" data-testid="caric-recap-total">{formatCurrency(sel.price_eur)}</dd></div>
              </dl>
              <p className="text-[11px] text-gray-500">{t('caric.recap.noSub')} · {t('caric.recap.oneTime')}</p>
              <div className="text-center pt-1">
                <button type="button" onClick={pay} disabled={!b64 || !consent || !rights || busy || (config && !config.enabled)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-pink-600 text-white font-bold hover:bg-pink-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="caric-pay">
                  <CreditCard className="w-4 h-4" aria-hidden="true" /> {busy && phase === 'paying' ? t('caric.redirecting') : t('caric.payTotal', { price: formatCurrency(sel.price_eur) })}
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center">{t('caric.securedBySumup')}</p>
            </section>
          </>
        )}

        {history.length > 0 && (
          <section className="space-y-2" data-testid="caric-history">
            <h2 className="text-white font-bold">{t('caric.history')}</h2>
            <ul className="divide-y divide-white/10 rounded-xl border border-white/10 bg-black/30">
              {history.map((h) => (
                <li key={h.session_id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-gray-300">{offerName(h.offer) || h.offer} · {imagesLabel((h.images || []).length)} · {h.created_at ? new Date(h.created_at).toLocaleDateString() : ''}</span>
                  <span className="flex items-center gap-3">
                    <button type="button" onClick={() => openHistory(h)} className="text-pink-300 hover:text-pink-200 text-xs">{t('caric.view')}</button>
                    <button type="button" onClick={() => removeCaricature(h.session_id)} className="text-gray-400 hover:text-red-300 text-xs" data-testid={`caric-del-${h.session_id}`}>{t('caric.delete')}</button>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default ClientCaricaturePage;
